import classNames from 'classnames';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOMCompat from '../../wui-core/src/reactCompat';
import Icon from '../../wui-icon/src';
import {KeyCode, isPlainObject, getNid} from '../../wui-core/src';
import { browser, getNodeChildren, toArray, warnOnlyTreeNode } from './utils/util';
import { TreeNodeProps, TreeNodeLinkDomProps, TreeNodesStates } from './iTree'
import {globalConfig} from '../../wui-provider/src';

const browserUa = typeof window !== 'undefined' ? browser(window.navigator) : '';
const ieOrEdge = /.*(IE|Edge).+/.test(browserUa);

const defaultTitle = '---';
const treeNodeDefaultBefore = 4;
const treeNodeDefaultAfter = 12;
const dragX = 26;

interface TreeNodeFC extends React.FC<TreeNodeProps> {
    isTreeNode?: number;
}

const TreeNode: TreeNodeFC = (props) => {

    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [dragNodeHighlight, setDragNodeHighlight] = useState<boolean>(false);

    // 使用 useRef 存储实例变量
    const lastExecutionTimeRef = useRef<number>(0);
    const initExpandRef = useRef<boolean>(false);
    const clickEventsRef = useRef<string[]>([]);
    const checkboxRef = useRef<HTMLSpanElement | null>(null);
    const treeNodeContentWrapperRef = useRef<HTMLAnchorElement | null>(null);
    const doubleClickFlagRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const transitionAppearRef = useRef<boolean>(false);

    // 同步选中和高亮状态
    const syncCheckedAndSelectedStatusFunc = useCallback((type: "onCheck" | "onSelect") => {
        if (type === 'onSelect') {
            checkboxRef.current && checkboxRef.current.click();
        } else {
            treeNodeContentWrapperRef.current?.click?.();
        }
        if (clickEventsRef.current.length >= 1) {
            clickEventsRef.current = [];
        }
    }, []);

    // 检查是否触发同步函数
    const checkIfTriggerSyncFunc = useCallback((type: "onCheck" | "onSelect") => {
        const { syncCheckedAndSelectedStatus, checked, selected } = props;
        if (syncCheckedAndSelectedStatus && clickEventsRef.current.length < 1) {
            if (checked && !selected && type === 'onCheck') {
                return false;
            }
            if (checked && !selected && type === 'onSelect') {
                return false;
            }
            clickEventsRef.current.push(type);
            syncCheckedAndSelectedStatusFunc(type);
        }
    }, [props.syncCheckedAndSelectedStatus, props.checked, props.selected, syncCheckedAndSelectedStatusFunc]);

    // 获取节点子元素
    const getNodeChildrenFn = useCallback(() => {
        const { children } = props;
        const originList = toArray(children as React.ReactElement[]).filter(node => node);
        const targetList = getNodeChildren(originList);
        if (originList.length !== targetList.length) {
            warnOnlyTreeNode();
        }
        return targetList;
    }, [props.children]);

    // onCheck 事件处理
    const onCheck = useCallback((e?: React.MouseEvent) => {
        const { isBigData } = props;
        const currentTime = Date.now();
        if (!lastExecutionTimeRef.current || currentTime - lastExecutionTimeRef.current >= 200) {
            if (isBigData) {
                ReactDOMCompat.startTransition(() => {
                    checkIfTriggerSyncFunc('onCheck');
                    props.root?.onCheck({ props, getNodeChildren: getNodeChildrenFn } as any, e as React.MouseEvent);
                });
            } else {
                checkIfTriggerSyncFunc('onCheck');
                props.root?.onCheck({ props, getNodeChildren: getNodeChildrenFn } as any, e as React.MouseEvent);
            }
            lastExecutionTimeRef.current = currentTime;
        } else if (currentTime - lastExecutionTimeRef.current < 200) {
            lastExecutionTimeRef.current = currentTime;
        }
    }, [props.isBigData, props.root, checkIfTriggerSyncFunc, getNodeChildrenFn]);

    // onSelect 事件处理
    const onSelect = useCallback((e?: React.MouseEvent) => {
        const { root, isBigData } = props;
        const currentTime = Date.now();
        if (!lastExecutionTimeRef.current || currentTime - lastExecutionTimeRef.current >= 200) {
            if (isBigData) {
                ReactDOMCompat.startTransition(() => {
                    checkIfTriggerSyncFunc('onSelect');
                    root?.onSelect({ props, getNodeChildren: getNodeChildrenFn } as any, e as React.MouseEvent);
                });
            } else {
                checkIfTriggerSyncFunc('onSelect');
                root?.onSelect({ props, getNodeChildren: getNodeChildrenFn } as any, e as React.MouseEvent);
            }
            lastExecutionTimeRef.current = currentTime;
        } else if (currentTime - lastExecutionTimeRef.current < 200) {
            lastExecutionTimeRef.current = currentTime;
        }
    }, [props.isBigData, props.root, checkIfTriggerSyncFunc, getNodeChildrenFn]);

    // 双击事件处理
    const onDoubleClick = (e: React.MouseEvent) => {
        doubleClickFlagRef.current && clearTimeout(doubleClickFlagRef.current);
        props.root?.onDoubleClick({ props: props } as any, e);
    };

    // 鼠标进入事件处理
    const onMouseEnter = (e: React.MouseEvent) => {
        e.preventDefault();
        props.root?.onMouseEnter(e, { props: props } as any);
    };

    // 鼠标离开事件处理
    const onMouseLeave = (e: React.MouseEvent) => {
        e.preventDefault();
        props.root?.onMouseLeave(e, { props: props } as any);
    };

    // 右键菜单事件处理
    const onContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        props.root?.onContextMenu(e, { props, getNodeChildren: getNodeChildrenFn } as any);
    };

    // 拖拽事件处理
    const onDragStart = (e: React.DragEvent) => {
        e.stopPropagation();
        setDragNodeHighlight(true);
        props.root?.onDragStart(e, { props: props } as any);
        try {
            e?.dataTransfer?.setData('text/plain', '');
        } finally {
            // empty
        }
    };

    const onDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        props.root?.onDragEnter(e, { props: props } as any);
    };

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        props.root?.onDragOver(e, { props: props } as any, treeNodeContentWrapperRef.current || undefined);
        return false;
    };

    const onDragLeave = (e: React.DragEvent) => {
        e.stopPropagation();
        props.root?.onDragLeave(e, { props: props } as any);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragNodeHighlight(false);
        props.root?.onDrop(e, { props: props } as any);
    };

    const onDragEnd = (e: React.DragEvent) => {
        e.stopPropagation();
        setDragNodeHighlight(false);
        props.root?.onDragEnd(e, { props: props } as any);
    };

    // 设置treeNodeContentWrapper的引用供父组件使用
    // useEffect(() => {
    //     if (treeNodeContentWrapperRef.current) {
    //         // 将treeNodeContentWrapper的引用存储到父组件可访问的位置
    //         (props as any).treeNodeContentWrapper = treeNodeContentWrapperRef.current;
    //     }
    // }, [props.root]);

    // 展开/收起事件处理
    const onExpand = useCallback((e?: React.MouseEvent) => {
        const { isBigData } = props;
        transitionAppearRef.current = true;
        let callbackPromise: undefined | Promise<any>;
        isBigData ? ReactDOMCompat.startTransition(() => {
            callbackPromise = props.root?.onExpand({ props, getNodeChildren: getNodeChildrenFn } as any, '', e);
        }) : callbackPromise = props.root?.onExpand({ props, getNodeChildren: getNodeChildrenFn } as any, '', e);
        if (callbackPromise && typeof callbackPromise === 'object') {
            setDataLoading(true);
            (callbackPromise as Promise<any>).then(() => {
                transitionAppearRef.current = true;
                setDataLoading(false);
            }, () => {
                transitionAppearRef.current = true;
                setDataLoading(false);
            });
        }
    }, [props.isBigData, props.root, getNodeChildrenFn]);

    // 替代 componentDidMount 和 componentDidUpdate
    useEffect(() => {
        if (!initExpandRef.current && props.autoExpand) {
            if (!props.expanded && props.isLeaf !== true) {
                onExpand();
                initExpandRef.current = true;
            }
        }
    }, [props.autoExpand, props.expanded, props.isLeaf, onExpand]);

    // 键盘事件处理
    const onKeyDown = (e: React.KeyboardEvent) => {
        props.root?.onKeyDown(e, { props: props } as any);
        if ([
            KeyCode.SPACE,
            KeyCode.DOWN,
            KeyCode.LEFT,
            KeyCode.RIGHT,
            KeyCode.UP
        ].includes(e.keyCode)) {
            e.preventDefault();
        }
    };

    const onSwitcherMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    const onCheckboxMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
    }, []);

    // ✅ 渲染拖拽图标
    const renderDragIcon = useMemo(() => {
        if (!props.draggable || !props.showDraggableIcon) return null;

        const clsPrefix = props.clsPrefix;
        const dragIconCls = {
            [`${clsPrefix}-drag-icon`]: true,
        };

        return (
            <span className={classNames(dragIconCls)}>
                <Icon type="uf-tuodong" />
            </span>
        );
    }, [props.draggable, props.clsPrefix, props.showDraggableIcon]);

    // ✅ 性能优化：使用 useMemo 缓存 renderSwitcher
    const renderSwitcher = useMemo(() => {
        // const iconType: string = expandedState === 'open' ? "uf-xiangxia1" : 'uf-triangle-right'
        const expandedState = props.expanded ? 'open' : 'close';
        const clsPrefix = props.clsPrefix;
        let stateIcon: any = <Icon className={`${clsPrefix}-switcher-state-icon ${clsPrefix}-switcher-state-icon-${props.dir}`} type="uf-xiangxia1" />;
        const switcherCls = {
            [`${clsPrefix}-switcher`]: true,
            ['icon-none']: true,
        };
        const switcherStyle = props.switcherStyle || undefined;
        if (!props.showLine) {
            switcherCls[`${clsPrefix}-noline_${expandedState}`] = true;
        } else if (props.pos === '0-0') {
            switcherCls[`${clsPrefix}-roots_${expandedState}`] = true;
        } else {
            switcherCls[`${clsPrefix}-first_${expandedState}`] = !!props.first;
            switcherCls[`${clsPrefix}-center_${expandedState}`] = !props.last;
            switcherCls[`${clsPrefix}-bottom_${expandedState}`] = !!props.last;
        }
        if (expandedState === 'open' && props.openIcon) {
            stateIcon = props.openIcon;
        }
        if (expandedState === 'close' && props.closeIcon) {
            stateIcon = props.closeIcon;
        }
        const fieldidProp = props.fieldidPrefix ? { fieldid: `${props.fieldidPrefix}_tree_switcher_${props.fieldid || props.eventKey || props.pos}` } : {}
        // switcherCls[stateIcon] = stateIcon;
        props.switcherClass ? switcherCls[`${props.switcherClass}`] = true : '';
        if (props.disabled && !props.mustExpandable) {
            switcherCls[`${clsPrefix}-switcher-disabled`] = true;
            return <span {...fieldidProp} className={classNames(switcherCls)} onClick={onExpand} style={switcherStyle}>{stateIcon}</span>;
        }
        // @ts-ignore
        return <span {...fieldidProp} uiclickable={props?.uirunmode === 'design' ? 'true' : undefined} className={classNames(switcherCls)} style={switcherStyle}
            onMouseDown={onSwitcherMouseDown} onClick={onExpand}>{stateIcon}</span>;
    }, [
        props.clsPrefix,
        props.dir,
        props.showLine,
        props.pos,
        props.first,
        props.last,
        props.openIcon,
        props.closeIcon,
        props.fieldidPrefix,
        props.fieldid,
        props.eventKey,
        props.switcherClass,
        props.disabled,
        props.mustExpandable,
        props.switcherStyle,
        props.uirunmode,
        onSwitcherMouseDown,
        onExpand
    ]);

    // 渲染选择框
    const renderCheckbox = useMemo(() => {
	    const disabled = props.disabled || props.disableCheckbox
	    const inverse = props.inverse
	    const clsPrefix = props.clsPrefix;
        let customEle = typeof props.checkable !== 'boolean' ? props.checkable : null;
	    const checkboxCls = {
	        [`${clsPrefix}-checkbox`]: true,
	        [`${clsPrefix}-checkbox-inverse`]: inverse,
            [`${clsPrefix}-checkbox-disabled`]: disabled,
            [`${clsPrefix}-checkbox-checked`]: props.checked,
            [`${clsPrefix}-checkbox-indeterminate`]: props.halfChecked,
	    };
	    let checkboxAttrs = {}
	    const fieldidCheckboxProp = props.fieldidPrefix ? { fieldid: `${props.fieldidPrefix}_tree_checkbox_${props.fieldid || props.eventKey || props.pos}`} : {}
	    if (props.getCheckboxAttrs && typeof props.getCheckboxAttrs === 'function') {
	        const obj = props.getCheckboxAttrs(props)
	        if (isPlainObject(obj)) { // 只有普通js对象可以传到dom上
	            checkboxAttrs = obj
	        }
	    }
	    if (disabled) {
	        return <span {...checkboxAttrs} className={classNames(checkboxCls)}>{customEle}</span>;
	    }
	    return (
            <span
                {...checkboxAttrs}
                {...fieldidCheckboxProp}
                ref={checkboxRef}
                className={classNames(checkboxCls)}
                onClick={onCheck}
                onMouseDown={onCheckboxMouseDown}
            >{customEle}</span>);
    }, [
        props.disabled,
        props.inverse,
        props.clsPrefix,
        props.checkable,
        props.checked,
        props.halfChecked,
        props.fieldidPrefix,
        props.fieldid,
        props.eventKey,
        props.pos,
        props.getCheckboxAttrs,
        props.switcherClass,
        onCheckboxMouseDown,
        onCheck
    ]);

    // 渲染子节点
    const renderChildren = () => {
	    const children = props.children && Array.isArray(props.children) ? props.children.filter(child => child) : props.children;
	    let newChildren = children;
	    // 确定所有子节点是否是TreeNode
	    let allTreeNode = false;
	    if (Array.isArray(children)) {
	        for (let index = 0; index < children.length; index++) {
	            let item = children[index];
	            if (!item) break;
	            allTreeNode = item.type.isTreeNode == 1;
	            if (!allTreeNode) {
	                // 当检查到子节点中有不是 TreeNode 的，则直接结束检查。同时不会渲染所有子节点
	                break;
	            }
	        }
	    } else if (children && (children as React.ReactElement).type && ((children as React.ReactElement).type as any).isTreeNode == 1) {
	        allTreeNode = true;
	    }
	    //  如果props.children的长度大于0才可以生成子对象
	    if (allTreeNode && React.Children.count(children)) {
	        const cls = {
	            [`${props.clsPrefix}-child-tree`]: true,
	            [`${props.clsPrefix}-child-tree-open`]: props.expanded,
	        };
	        // const animProps: AnimateProps = {};
	        // if (props.openTransitionName) {
	        //     animProps.transitionName = props.openTransitionName;
	        // } else if (typeof props.openAnimation === 'object') {
	        //     animProps.animation = Object.assign({}, props.openAnimation);
	        //     if (!this.transitionAppear) {
	        //         delete animProps.animation?.appear;
	        //     }
	        // }
	        newChildren = (
	            !props.expanded ? null : <ul className={classNames(cls)} data-expanded={props.expanded}>
	                {React.Children.map(children, (item, index) => {
	                    return props.root?.renderTreeNode(item, index, props.pos);
	                })}
	            </ul>
	        );
	    }
        transitionAppearRef.current = false;
        return newChildren;
    };

    // 判断是否为叶子节点
    const checkIsLeaf = useCallback(() => {
        const { isLeaf, loadData } = props;
        if (isLeaf === false || isLeaf === true) {
            return isLeaf;
        } else {
            const hasChildren = getNodeChildrenFn().length !== 0;
            return (!loadData && !hasChildren);
        }
    }, [props.isLeaf, props.loadData, getNodeChildrenFn]);

    // 渲染树节点内部要展示内容的dom结构
    const renderTreeNodeContent = (iconState: string, expandedState: string) => {
        let { onRenderTitle, ext, clsPrefix, 'title': titleContent, titleClass, selected, contentCls, titleStyle, fieldidPrefix, fieldid, eventKey, pos, loadData, dropPositionX,
            showIcon, icon, disabled, _dropTrigger, selectable, draggable, focusable, tabIndexKey, tabIndexValue, onResetFocusKey, expandOnClickNode, isLeaf } = props;
        const content = (typeof onRenderTitle == 'function') ? onRenderTitle(ext, props) : titleContent; // ext来自tree.props.treeData的json原始数据 同 props 对象是有区别的，原始json数据由使用方传入，所以优先传回给使用方，同时保障了向下兼容。
        // const clsPrefix = clsPrefix;
        titleClass = titleClass ? clsPrefix + '-title' + ' ' + titleClass : clsPrefix + '-title';
        const iconEleCls = {
            [`${clsPrefix}-iconEle`]: true,
            [`${clsPrefix}-icon_loading`]: dataLoading,
            [`${clsPrefix}-icon__${iconState}`]: true
        };
        const fieldidContentProp = fieldidPrefix ? { fieldid: `${fieldidPrefix}_tree_content_${fieldid || eventKey || pos}` } : {}
        const fieldidIconProp = fieldidPrefix ? { fieldid: `${fieldidPrefix}_tree_icon_${fieldid || eventKey || pos}` } : {}
        const fieldidTitleProp = fieldidPrefix ? { fieldid: `${fieldidPrefix}_tree_title_${fieldid || eventKey || pos}` } : {}
        // const icon = (props.showIcon || props.loadData && dataLoading) ?
        //   <span className={classNames(iconEleCls)}></span> : null;
        let _icon;
        const isLoading = loadData && dataLoading
        if (isLoading) { // loading优先级最高
            _icon = <span {...fieldidIconProp} className={classNames(iconEleCls)}></span>
        } else if (showIcon && !icon) { // 没传icon，不是loading状态，不显示这个icon
            _icon = null
        } else if (showIcon && icon) {
            _icon = <span
                {...fieldidIconProp}
                className={classNames(
                    `${clsPrefix}-iconEle`,
                    `${clsPrefix}-icon__customize`,
                )}
            >
                {typeof icon === 'function' ?
                    icon(props) : icon
                }
            </span>
        }
        const title = <span {...fieldidTitleProp} className={titleClass} style={titleStyle}>{content}</span>;
        // const dot = iconState === 'docu' ? <span className={classNames(`${clsPrefix}-treenode-dot`)} /> : null
        const wrap = `${clsPrefix}-node-content-wrapper`;
        let domProps: TreeNodeLinkDomProps = {
            className: `${wrap} ${wrap}-${iconState === expandedState ? iconState : 'normal'}`
        };
        if (selected) { // 只要被选择了，就有这个类名
            domProps.className += ` ${clsPrefix}-node-selected`;
        }
        if (contentCls) {
            domProps.className += ` ${contentCls}`;
        }
        if (!disabled) {
            if (!_dropTrigger && dragNodeHighlight && !domProps.className.includes(`${clsPrefix}-node-selected`)) {
                domProps.className += ` ${clsPrefix}-node-selected`;
            }

            if (props.root?.onDoubleClick) {
                domProps.onDoubleClick = onDoubleClick;
            }
            if (props.root?.onContextMenu) {
                domProps.onContextMenu = onContextMenu;
            }
            if (props.root?.onMouseEnter) {
                domProps.onMouseEnter = onMouseEnter;
            }
            if (props.root?.onMouseLeave) {
                domProps.onMouseLeave = onMouseLeave;
            }

            if (draggable) {
                domProps.className += ' draggable';
                if (ieOrEdge) {
                    // ie bug!
                    domProps.href = '#';
                }
                domProps.draggable = true;
                domProps['aria-grabbed'] = true;
                domProps.onDragStart = onDragStart;
            }
        }
        domProps.onClick = (e: React.MouseEvent) => {
            e.preventDefault();
            if (expandOnClickNode && !isLeaf) {
                onExpand(e);
            }
            if (selectable && !disabled) {
                onSelect(e);
            }
        };
        // 设置tabIndex
        if (focusable) {
            domProps.onKeyDown = onKeyDown;
            domProps.tabIndex = -1;
            if (tabIndexKey) {
                if (eventKey == tabIndexKey) {
                    domProps.tabIndex = tabIndexValue;
                }
            } else if (pos == '0-0') {
                domProps.tabIndex = tabIndexValue;
            }
            domProps.onBlur = onResetFocusKey || (() => {})
        }
        let len = 0;
        if (dropPositionX?.startsWith('left')) {
            len = +dropPositionX.split('_')[1]
        }
        const nodeBeforeStyle = len ? {left: `${-len * dragX + treeNodeDefaultBefore}px`} : {};
        const nodeAfterStyle = len ? {left: `${-len * dragX + treeNodeDefaultAfter}px`, width: `calc(100% + ${len * dragX}px)`} : {};
        if (draggable) {
            domProps = {...domProps,
                onDragEnter: onDragEnter,
                onDragOver: onDragOver,
                onDragLeave: onDragLeave,
                onDrop: onDrop,
                onDragEnd: onDragEnd
            }
        }
        return (
            <a
                {...fieldidContentProp}
                ref={treeNodeContentWrapperRef}
                pos={pos}
                title={typeof content === 'string' ? content : ''}
                {...domProps}
            >
                {_icon}
                {title}
                {
                    draggable ? <>
                        <span style={nodeBeforeStyle} className={`${clsPrefix}-node-before`}></span>
                        <span style={nodeAfterStyle} className={`${clsPrefix}-node-after`}></span></> : null
                }
            </a>
        );
    };

    // 渲染没有展开标志的dom结构
    const treeNodeSwitcher = useMemo(() => {
        const clsPrefix = props.clsPrefix;
	    const cls = {
	        [`${clsPrefix}-switcher`]: true,
	        [`${clsPrefix}-switcher-noop`]: true,
	    };
	    if (props.showLine) {
	        // console.log('line---------');
	        cls[`${clsPrefix}-center_docu`] = !props.last;
	        cls[`${clsPrefix}-bottom_docu`] = !!props.last;
	    } else {
	        cls[`${clsPrefix}-noline_docu`] = true;
	    }
        return <span className={classNames(cls)}></span>;
    }, [props.clsPrefix, props.showLine, props.last]);

    // 获取树节点最外层li类名
    const getLiClassNames = (expandedState: string, canRenderSwitcher: boolean, iconState: string) => {
	    const { dropPositionX, clsPrefix, last, first, filterTreeNode, selected, focused, className } = props;
	    const baseCls = `${clsPrefix}-treenode`;
	    let disabledCls = ''; // 禁用类名
	    let dragOverCls = ''; // 拖拽类名
	    let switcherDashedLineCls = canRenderSwitcher && !props.last && expandedState === 'close' ? `${clsPrefix}-treenode-line` : ''
	    let lastLiCls = last ? `${clsPrefix}-treenode-last` : ''
	    let firstLiCls = first ? `${clsPrefix}-treenode-first` : ''
	    let isLeafCls = iconState === 'docu' ? `${clsPrefix}-treenode-leaf` : `${clsPrefix}-treenode-parent`
	    if (props.disabled) {
	        disabledCls = `${clsPrefix}-treenode-disabled`;
	    } else if (props.dragOver) {
	        dragOverCls = 'drag-over';
	    } else if (props.dragOverGapTop) {
	        dragOverCls = 'drag-over-gap-top';
	    } else if (props.dragOverGapBottom) {
	        dragOverCls = 'drag-over-gap-bottom';
	    }
        const dropX = dropPositionX ? `${clsPrefix}-treenode-drag-x-${dropPositionX}` : null;
        const filterCls = filterTreeNode ? (filterTreeNode({ props: props } as any) ? `${clsPrefix}-treenode-filter-node` : '') : ''; // 过滤类名
	    const selectedCls = selected ? `${clsPrefix}-treenode-selected` : ''; // 被选择类名
	    const focusedCls = focused ? `${clsPrefix}-treenode-focused` : ''; // 聚焦状态类名
	    const expandedCls = `${clsPrefix}-treenode-${expandedState}`; // 展开状态类名
	    return classNames(className, baseCls, disabledCls, dragOverCls, filterCls, selectedCls, focusedCls, dropX,
            expandedCls, switcherDashedLineCls, lastLiCls, firstLiCls, isLeafCls) // TODO: 减少一些类名
    };

    // 获取 li 属性
    const getLiProps = useCallback(() => {
	    let liProps = {};
	    if (props.liAttr) {
	        liProps = Object.assign({}, props.liAttr);
	    }
        return liProps;
    }, []);

    // 渲染带有竖向连接线的小格子（使用 useMemo 优化性能）
    const indentsElement = useMemo(() => {
        if (!props.showLine) return null;

        const level = props.treeNodesStatesKeysInfo?.[props.eventKey as string]; // 获取当前节点的位置信息，如0-3-2-5-9
        if (!level) return null;

        const getGlobalDirection = globalConfig().getGlobalDirection;
        const direction = getGlobalDirection();
        const levelsArr = level.split('-');
        const levels = levelsArr.length - 1; // 获得距离最外层节点的层级数量
        const res = [];

        if (levels > 0) {
            const ulPadding = 26; // 当前设计规范的padding距离，也是一个格子的宽度
            for (let i = 0; i < levels - 1; i++) { // 自己的一层没有格子
                const parentPos = levelsArr.slice(0, levels - i); // 父节点（祖先节点）的位置，如父组件位置是0-3-2-5，祖父节点位置是0-3-2...
                const parentInfo = (props.treeNodesStates as TreeNodesStates)[parentPos.join('-')];
                const isParentNodeLast = parentInfo ? !!parentInfo?.siblingPosition?.last : false; // 父节点是否是所在层级的最后一个节点，是最后一个的话，增加end类名，没有向下的连接线
                res.unshift(<span key={`indent-${i}`} className={`${props.clsPrefix}-indent ${props.clsPrefix}-indent-${isParentNodeLast ? 'end' : 'start'}`} />);
            }
            return <span style={{ [direction === 'rtl' ? 'right' : 'left']: (levels - 1) * -1 * ulPadding }} className={`${props.clsPrefix}-indents`}>
                {res}
            </span>;
        } else {
            return null;
        }
    }, [
        props.showLine,
        props.eventKey,
        props.treeNodesStatesKeysInfo,
        props.treeNodesStates,
        props.clsPrefix
    ]);

    // Render logic
    const expandedState = props.expanded ? 'open' : 'close';
    let iconState = expandedState;
    let canRenderSwitcher = true;
    if (checkIsLeaf()) {
        canRenderSwitcher = false;
        iconState = 'docu';
    }
    const fieldidProp = props.fieldidPrefix ? { fieldid: `${props.fieldidPrefix}_option_${props.fieldid || props.eventKey || props.pos}` } : {}
    let adapterNid = getNid(props)
    let _className = getLiClassNames(expandedState, canRenderSwitcher, iconState)
    return (
        <li
            {...getLiProps()}
            style={props.style}
            className={_className}
            {...fieldidProp}
            {...adapterNid}
        >
            {
                props.showLine ? <div className={`${props.clsPrefix}-line`}>
                    {canRenderSwitcher ? <>{renderDragIcon}{renderSwitcher}</> : <>{treeNodeSwitcher}{renderDragIcon}</>}
                    <span className={`${props.clsPrefix}-treenode-line`} />
                    {indentsElement}
                    {props.checkable && props.visibleCheckbox ? renderCheckbox : null}
                    {renderTreeNodeContent(iconState, expandedState)}
                </div>
                    : <div className={`${props.clsPrefix}-treenode-line-content`}>
                        {canRenderSwitcher ? <>{renderDragIcon}{renderSwitcher}</> : <>{treeNodeSwitcher}{renderDragIcon}</>}
                        {props.checkable && props.visibleCheckbox ? renderCheckbox : null}
                        {renderTreeNodeContent(iconState, expandedState)}
                    </div>
            }
            {renderChildren()}
        </li>
    );
};

// 定义默认属性
TreeNode.defaultProps = {
    title: defaultTitle,
    tabIndexValue: 0,
    mustExpandable: false,
    visibleCheckbox: true
};

// 添加静态属性
TreeNode.isTreeNode = 1;

// ==================== 性能优化：使用 React.memo ====================
// 使用默认的浅比较策略，只在 props 引用变化时重新渲染
// 这是一个保守但安全的优化策略
const MemoizedTreeNode = React.memo(TreeNode);

// 保持静态属性
(MemoizedTreeNode as any).isTreeNode = 1;
(MemoizedTreeNode as any).defaultProps = TreeNode.defaultProps;

export default MemoizedTreeNode;